import React, { useState, useEffect } from "react";
import {
  Card,
  Table,
  Select,
  Button,
  Row,
  Col,
  message,
  Spin,
  Typography,
  Space,
  Tag,
} from "antd";
import {
  SearchOutlined,
  ExpandAltOutlined,
  ShrinkOutlined,
  FileTextOutlined,
} from "@ant-design/icons";
import API from "../../api";
import dayjs from "dayjs";

const { Title } = Typography;
const { Option } = Select;

const CommissionReport = () => {
  const [loading, setLoading] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [commissions, setCommissions] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [expandedRows, setExpandedRows] = useState(new Set());

  // Fetch employees
  const getEmployees = async () => {
    try {
      const response = await API.get("/employee");
      setEmployees(response?.data || []);
    } catch (err) {
      console.error("Error fetching employees:", err);
      message.error("Failed to load employees.");
    }
  };

  // Fetch commission reports
  const fetchCommissionReports = async (employeeId) => {
    if (!employeeId) {
      setCommissions([]);
      return;
    }

    setLoading(true);
    try {
      const response = await API.get("/employee-commissions/paginated", {
        params: {
          employeeId,
          page: 1,
          limit: 1000, // Get all commissions for the employee
        },
      });

      const commissionsData = response?.data?.commissions || [];
      
      // Populate sales data for each commission
      const commissionsWithSales = await Promise.all(
        commissionsData.map(async (commission) => {
          try {
            // Fetch sales details for this commission using individual API calls
            const salesDetails = await Promise.all(
              commission.saleIds.map(async (saleId) => {
                try {
                  const salesResponse = await API.get(`/sales/${saleId}`);
                  return salesResponse?.data || null;
                } catch (err) {
                  console.error(`Error fetching sale ${saleId}:`, err);
                  return null;
                }
              })
            );
            
            return {
              ...commission,
              salesDetails: salesDetails.filter(sale => sale !== null)
            };
          } catch (err) {
            console.error("Error fetching sales for commission:", err);
            return {
              ...commission,
              salesDetails: []
            };
          }
        })
      );

      setCommissions(commissionsWithSales);
    } catch (err) {
      console.error("Error fetching commission reports:", err);
      message.error("Failed to load commission reports.");
    } finally {
      setLoading(false);
    }
  };

  // Handle employee selection
  const handleEmployeeChange = (value) => {
    setSelectedEmployee(value);
    fetchCommissionReports(value);
  };

  // Toggle row expansion
  const toggleRowExpansion = (recordKey) => {
    const newExpandedRows = new Set(expandedRows);
    if (newExpandedRows.has(recordKey)) {
      newExpandedRows.delete(recordKey);
    } else {
      newExpandedRows.add(recordKey);
    }
    setExpandedRows(newExpandedRows);
  };

  // Main table columns
  const columns = [
    {
      title: "Sl. No",
      key: "index",
      width: 80,
      render: (_, record, index) => index + 1,
    },
    {
      title: "Total Sales",
      dataIndex: "totalSalesAmount",
      key: "totalSalesAmount",
      width: 150,
      render: (amount) => (
        <span style={{ fontWeight: "bold", color: "#1890ff" }}>
          ₹{new Intl.NumberFormat().format(amount || 0)}
        </span>
      ),
    },
    {
      title: "Comm. Type",
      dataIndex: "commissionType",
      key: "commissionType",
      width: 130,
      render: (type) => (
        <Tag color={type === "percentage" ? "blue" : "green"}>
          {type === "percentage" ? "Percentage" : "Fixed Amount"}
        </Tag>
      ),
    },
    {
      title: "Comm. %",
      dataIndex: "commissionPercentage",
      key: "commissionPercentage",
      width: 120,
      render: (percentage, record) => {
        if (record.commissionType === "percentage") {
          return <span>{percentage}%</span>;
        }
        return <span style={{ color: "#999" }}>-</span>;
      },
    },
    {
      title: "Paid Amount",
      dataIndex: "amount",
      key: "amount",
      width: 130,
      render: (amount) => (
        <span style={{ fontWeight: "bold", color: "#52c41a" }}>
          ₹{new Intl.NumberFormat().format(amount || 0)}
        </span>
      ),
    },
    {
      title: "Paid Date",
      dataIndex: "date",
      key: "date",
      width: 120,
      render: (date) => dayjs(date).format("DD-MM-YYYY"),
    },
    {
      title: "Month",
      dataIndex: "month",
      key: "month",
      width: 150,
      render: (month) => {
        if (!month) return <Tag color="purple">N/A</Tag>;
        
        // Parse month string (YYYY-MM format)
        const [year, monthNum] = month.split('-');
        const monthNames = [
          'January', 'February', 'March', 'April', 'May', 'June',
          'July', 'August', 'September', 'October', 'November', 'December'
        ];
        
        const monthName = monthNames[parseInt(monthNum) - 1] || 'Unknown';
        const displayText = `${monthName} ${year}`;
        
        return (
          <Tag color="purple" title={`${month} (${displayText})`}>
            {displayText}
          </Tag>
        );
      },
    },
    {
      title: "Pay Mode",
      dataIndex: "paymentMethod",
      key: "paymentMethod",
      width: 100,
      render: (method) => {
        const methodColors = {
          cash: "green",
          upi: "blue",
          bank_transfer: "purple",
          other: "orange",
        };
        return (
          <Tag color={methodColors[method] || "default"}>
            {method?.toUpperCase() || "N/A"}
          </Tag>
        );
      },
    },
    {
      title: "Paid By",
      dataIndex: "createdBy",
      key: "createdBy",
      width: 120,
      render: (createdBy) => {
        // You might want to fetch user details here
        return <span>{createdBy?.name || "Unknown"}</span>;
      },
    },
    {
      title: "Actions",
      key: "actions",
      width: 100,
      render: (_, record) => (
        <Button
          type="text"
          icon={expandedRows.has(record._id) ? <ShrinkOutlined /> : <ExpandAltOutlined />}
          onClick={() => toggleRowExpansion(record._id)}
          style={{ color: "#1890ff" }}
        >
          {expandedRows.has(record._id) ? "Collapse" : "Expand"}
        </Button>
      ),
    },
  ];

  // Expanded row content
  const expandedRowRender = (record) => {
    if (!record.salesDetails || record.salesDetails.length === 0) {
      return (
        <div style={{ padding: "16px", textAlign: "center", color: "#999" }}>
          No sales details available
        </div>
      );
    }

    const salesColumns = [
      {
        title: "Invoice ID",
        dataIndex: "invoiceId",
        key: "invoiceId",
        width: 120,
      },
      {
        title: "Customer",
        dataIndex: "customerId",
        key: "customerId",
        width: 150,
        render: (customer) => customer?.name || "Walk-in Customer",
      },
      {
        title: "Invoice Amount",
        dataIndex: "totalAmount",
        key: "totalAmount",
        width: 130,
        render: (amount) => (
          <span style={{ fontWeight: "bold" }}>
            ₹{new Intl.NumberFormat().format(amount || 0)}
          </span>
        ),
      },
      {
        title: "Date",
        dataIndex: "createdAt",
        key: "createdAt",
        width: 100,
        render: (date) => dayjs(date).format("DD-MM-YYYY"),
      },
      {
        title: "Status",
        dataIndex: "status",
        key: "status",
        width: 100,
        render: (status) => (
          <Tag color={status === "completed" ? "green" : "red"}>
            {status?.toUpperCase()}
          </Tag>
        ),
      },
    ];

    return (
      <div style={{ margin: "0 16px" }}>
        <Title level={5} style={{ marginBottom: "16px" }}>
          <FileTextOutlined /> Sales Details ({record.salesDetails.length} invoices)
        </Title>
        <Table
          columns={salesColumns}
          dataSource={record.salesDetails}
          pagination={false}
          size="small"
          rowKey="_id"
        />
      </div>
    );
  };

  useEffect(() => {
    getEmployees();
  }, []);

  return (
    <div style={{ padding: "24px" }}>
      <Card>
        <Title level={2}>
          <FileTextOutlined /> Commission Reports
        </Title>
        
        <Row gutter={[16, 16]} style={{ marginBottom: "24px" }}>
          <Col xs={24} sm={12} md={8}>
            <Select
              placeholder="Select Employee"
              style={{ width: "100%" }}
              value={selectedEmployee}
              onChange={handleEmployeeChange}
              allowClear
              showSearch
              optionFilterProp="children"
              filterOption={(input, option) =>
                option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
              }
            >
              {employees.map((employee) => (
                <Option key={employee._id} value={employee._id}>
                  {employee.name}
                </Option>
              ))}
            </Select>
          </Col>
          <Col xs={24} sm={12} md={4}>
            <Button
              type="primary"
              icon={<SearchOutlined />}
              onClick={() => fetchCommissionReports(selectedEmployee)}
              disabled={!selectedEmployee}
              style={{ width: "100%" }}
            >
              Search
            </Button>
          </Col>
        </Row>

        {selectedEmployee && (
          <div style={{ marginBottom: "16px" }}>
            <Space>
              <strong>Selected Employee:</strong>
              <span style={{ color: "#1890ff" }}>
                {employees.find(emp => emp._id === selectedEmployee)?.name || "Unknown"}
              </span>
              <strong>Total Commissions:</strong>
              <span style={{ color: "#52c41a" }}>
                {commissions.length} records
              </span>
            </Space>
          </div>
        )}

        <Spin spinning={loading}>
          <Table
            columns={columns}
            dataSource={commissions}
            rowKey="_id"
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) =>
                `${range[0]}-${range[1]} of ${total} records`,
            }}
            expandable={{
              expandedRowKeys: Array.from(expandedRows),
              expandedRowRender: expandedRowRender,
              onExpand: (expanded, record) => {
                toggleRowExpansion(record._id);
              },
            }}
            scroll={{ x: 1200 }}
            size="middle"
          />
        </Spin>

        {!loading && commissions.length === 0 && selectedEmployee && (
          <div style={{ textAlign: "center", padding: "40px", color: "#999" }}>
            <FileTextOutlined style={{ fontSize: "48px", marginBottom: "16px" }} />
            <div>No commission records found for the selected employee.</div>
          </div>
        )}

        {!selectedEmployee && (
          <div style={{ textAlign: "center", padding: "40px", color: "#999" }}>
            <SearchOutlined style={{ fontSize: "48px", marginBottom: "16px" }} />
            <div>Please select an employee to view commission reports.</div>
          </div>
        )}
      </Card>
    </div>
  );
};

export default CommissionReport;
